/**
 * Centralized error monitoring (Sentry SaaS).
 *
 * Decisões arquiteturais (Diretor — 27/04/2026):
 * - Sentry SaaS plano free (5k errors/mês).
 * - PII scrubbing agressivo por padrão — nunca enviar dados sensíveis.
 * - Telemetria desligada em desenvolvimento (apenas console).
 * - VITE_SENTRY_DSN e SENTRY_AUTH_TOKEN gerenciados via Vercel Env Vars
 *   (nunca commitar credenciais).
 */
import * as Sentry from "@sentry/react";

const isProd = import.meta.env.PROD;

/**
 * Inicializa o Sentry. Deve ser chamado uma única vez, antes de qualquer
 * componente React montar (no main.tsx).
 */
export const initMonitoring = () => {
  if (!isProd || !import.meta.env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || "unknown",

    // Performance: amostrar 10% das transações para não sobrecarregar a quota free.
    tracesSampleRate: 0.1,

    // PII: scrubbing agressivo — remover campos sensíveis antes do envio.
    beforeSend(event) {
      if (event.request?.data) {
        const sensitiveFields = [
          "cpf",
          "cnpj",
          "email",
          "phone",
          "document_number",
          "password",
          "token",
          "pix_key",
          "account_number",
        ];
        if (typeof event.request.data === "object" && event.request.data !== null) {
          const data = event.request.data as Record<string, unknown>;
          sensitiveFields.forEach((field) => {
            delete data[field];
          });
        }
      }
      return event;
    },

    // Tags seguras (sem PII) para filtrar no dashboard Sentry.
    initialScope: {
      tags: {
        app: "gonx",
      },
    },
  });
};

export const monitoring = {
  /**
   * Identifica o usuário após login. Apenas IDs internos — nunca enviar
   * email, nome, CPF/CNPJ ou outros dados pessoais.
   */
  setUser: (userId: string, companyId: string, role: string) => {
    if (!isProd) return;
    Sentry.setUser({ id: userId });
    Sentry.setTag("company_id", companyId);
    Sentry.setTag("user_role", role);
  },

  /**
   * Limpa a identidade do usuário após logout.
   */
  clearUser: () => {
    if (!isProd) return;
    Sentry.setUser(null);
    Sentry.setTag("company_id", null);
    Sentry.setTag("user_role", null);
  },

  /**
   * Captura uma exceção com contexto adicional. Em dev, apenas log no console.
   */
  captureException: (error: unknown, context?: Record<string, unknown>) => {
    if (!isProd) {
      console.error("[monitoring]", error, context);
      return;
    }
    Sentry.withScope((scope) => {
      if (context) scope.setExtras(context);
      Sentry.captureException(error);
    });
  },

  /**
   * Captura um warning não-fatal (dado inconsistente, condição não encontrada,
   * etc.). Útil para observabilidade sem alarmar como erro.
   */
  captureWarning: (message: string, context?: Record<string, unknown>) => {
    if (!isProd) {
      console.warn("[monitoring]", message, context);
      return;
    }
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      if (context) scope.setExtras(context);
      Sentry.captureMessage(message);
    });
  },

  /**
   * Tag de módulo para filtrar erros por área do sistema (financeiro, vendas,
   * fiscal, etc.) no dashboard Sentry.
   */
  setModule: (module: string) => {
    if (!isProd) return;
    Sentry.setTag("module", module);
  },
};

/** Atalhos nomeados (padrão de import em módulos) */
export const captureException = monitoring.captureException;
export const captureWarning = monitoring.captureWarning;
